import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, User, MapPin, Building, Zap, Calendar, FileText, FileSpreadsheet } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface HierarchyNode {
  id: string;
  type: 'customer' | 'site' | 'account' | 'meter' | 'month' | 'bill';
  name: string;
  children: HierarchyNode[];
  data?: any;
  expanded?: boolean;
}

export const ArchiveHierarchyView = () => {
  const { user } = useAuth();
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const buildHierarchy = async () => {
      setIsLoading(true);
      if (!user?.id) {
        setHierarchy([]);
        setIsLoading(false);
        return;
      }

      // Fetch archived bills for this user only (drive the tree from bills)
      let billsData: any[] = [];
      try {
        const { data, error } = await supabase
          .from('bills')
          .select('id, account_id, site_id, meter_id, bill_date, billing_period_start, billing_period_end, created_at, amount, currency, pdf_url, csv_url, customer_name_raw, customer_name_normalized, account_number_raw, account_number_normalized, stage')
          .eq('user_id', user.id)
          .eq('stage', 'archive');

        if (error) throw error;
        billsData = data || [];
      } catch (error) {
        console.error('Error fetching archived bills:', error);
        billsData = [];
      }

      const toSlug = (s: string) => s.toLowerCase().replace(/\s+/g, '-');
      const shortId = (id?: string | null) => (id ? String(id).slice(0, 8) : 'unknown');
      const formatCurrency = (amount?: number | string | null, currency = 'USD') => {
        if (amount == null) return '—';
        const num = typeof amount === 'string' ? parseFloat(amount) : amount;
        try {
          return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(num);
        } catch {
          return `$${(num ?? 0).toFixed(2)}`;
        }
      };

      // Build hierarchy: Customer → Site → Account → Meter → Month → Bill
      const customerMap = new Map<string, HierarchyNode>();

      for (const item of billsData) {
        const customerName = item.customer_name_normalized || item.customer_name_raw || 'Unknown Customer';
        const siteKey = item.site_id || 'unknown-site';
        const siteLabel = item.site_id ? `Site ${shortId(item.site_id)}` : 'Unknown Site';
        const accountKey = item.account_id || (item.account_number_normalized || item.account_number_raw || 'unknown-account');
        const accountLabel = item.account_number_normalized || item.account_number_raw || (item.account_id ? `Account ${shortId(item.account_id)}` : 'Unknown Account');
        const meterKey = item.meter_id || 'unknown-meter';
        const meterLabel = item.meter_id ? `Meter ${shortId(item.meter_id)}` : 'Unknown Meter';

        const monthSource = item.billing_period_start || item.billing_period_end || item.bill_date || item.created_at;
        const dt = monthSource ? new Date(monthSource) : new Date();
        const monthKey = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = dt.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });

        // customer
        const customerId = `customer-${toSlug(customerName)}`;
        let customerNode = customerMap.get(customerId);
        if (!customerNode) {
          customerNode = { id: customerId, type: 'customer', name: customerName, children: [] };
          customerMap.set(customerId, customerNode);
        }

        // site
        const siteId = `${customerId}-site-${siteKey}`;
        let siteNode = customerNode.children.find((c) => c.id === siteId);
        if (!siteNode) {
          siteNode = { id: siteId, type: 'site', name: siteLabel, children: [] };
          customerNode.children.push(siteNode);
        }

        // account
        const accountId = `${siteId}-account-${accountKey}`;
        let accountNode = siteNode.children.find((c) => c.id === accountId);
        if (!accountNode) {
          accountNode = { id: accountId, type: 'account', name: accountLabel, children: [] };
          siteNode.children.push(accountNode);
        }

        // meter
        const meterId = `${accountId}-meter-${meterKey}`;
        let meterNode = accountNode.children.find((c) => c.id === meterId);
        if (!meterNode) {
          meterNode = { id: meterId, type: 'meter', name: meterLabel, children: [] };
          accountNode.children.push(meterNode);
        }

        // month
        const monthId = `${meterId}-month-${monthKey}`;
        let monthNode = meterNode.children.find((c) => c.id === monthId);
        if (!monthNode) {
          monthNode = { id: monthId, type: 'month', name: monthLabel, children: [] };
          meterNode.children.push(monthNode);
        }

        // bill (leaf)
        const amountStr = formatCurrency(item.amount, item.currency || 'USD');
        const billLabelDate = item.bill_date || item.billing_period_end || item.billing_period_start || '';
        const billName = `${billLabelDate} • ${amountStr}`;
        const billId = `${monthId}-bill-${item.id}`;
        monthNode.children.push({ id: billId, type: 'bill', name: billName, children: [], data: item });
      }

      // Sorting
      const sortByNameAsc = (a: HierarchyNode, b: HierarchyNode) => a.name.localeCompare(b.name);
      const sortMonthsDesc = (a: HierarchyNode, b: HierarchyNode) => {
        const getKey = (n: HierarchyNode) => {
          const m = n.id.match(/month-(\d{4}-\d{2})/);
          return m ? m[1] : '0000-00';
        };
        return getKey(b).localeCompare(getKey(a));
      };
      const sortBillsDesc = (a: HierarchyNode, b: HierarchyNode) => {
        const da = new Date(a.data?.billing_period_end || a.data?.bill_date || a.data?.created_at || 0).getTime();
        const db = new Date(b.data?.billing_period_end || b.data?.bill_date || b.data?.created_at || 0).getTime();
        return db - da;
      };

      const customers = Array.from(customerMap.values()).sort(sortByNameAsc);
      customers.forEach((customer) => {
        customer.children.sort(sortByNameAsc); // sites
        customer.children.forEach((site) => {
          site.children.sort(sortByNameAsc); // accounts
          site.children.forEach((account) => {
            account.children.sort(sortByNameAsc); // meters
            account.children.forEach((meter) => {
              meter.children.sort(sortMonthsDesc); // months
              meter.children.forEach((month) => {
                month.children.sort(sortBillsDesc); // bills
              });
            });
          });
        });
      });

      setHierarchy(customers);
      setIsLoading(false);
    };

    buildHierarchy();
  }, [user?.id]);

  const toggleExpanded = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

const getNodeIcon = (type: string) => {
  switch (type) {
    case 'customer': return <User className="h-4 w-4 text-muted-foreground" />;
    case 'site': return <MapPin className="h-4 w-4 text-muted-foreground" />;
    case 'account': return <Building className="h-4 w-4 text-muted-foreground" />;
    case 'meter': return <Zap className="h-4 w-4 text-muted-foreground" />;
    case 'month': return <Calendar className="h-4 w-4 text-muted-foreground" />;
    case 'bill': return <FileText className="h-4 w-4 text-muted-foreground" />;
    default: return <FileText className="h-4 w-4 text-muted-foreground" />;
  }
};

const getNodeBadge = (node: HierarchyNode) => {
  switch (node.type) {
    case 'customer': {
      const totalSites = node.children.length;
      return <Badge variant="outline">{totalSites} site{totalSites !== 1 ? 's' : ''}</Badge>;
    }
    case 'site': {
      const totalAccounts = node.children.length;
      return <Badge variant="outline">{totalAccounts} account{totalAccounts !== 1 ? 's' : ''}</Badge>;
    }
    case 'account': {
      const totalMeters = node.children.length;
      return <Badge variant="outline">{totalMeters} meter{totalMeters !== 1 ? 's' : ''}</Badge>;
    }
    case 'meter': {
      const totalMonths = node.children.length;
      return <Badge variant="outline">{totalMonths} month{totalMonths !== 1 ? 's' : ''}</Badge>;
    }
    case 'month': {
      const totalBills = node.children.length;
      return <Badge variant="outline">{totalBills} bill{totalBills !== 1 ? 's' : ''}</Badge>;
    }
    case 'bill':
      return <Badge variant="outline">Archived</Badge>;
    default:
      return null;
  }
};

  const renderNode = (node: HierarchyNode, depth: number = 0) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const paddingLeft = depth * 24;

    return (
      <div key={node.id} className="space-y-1">
        <div 
          className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded cursor-pointer"
          style={{ paddingLeft: `${paddingLeft + 8}px` }}
          onClick={() => hasChildren && toggleExpanded(node.id)}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : (
            <div className="w-4" />
          )}
          
          {getNodeIcon(node.type)}
          
          <span className="font-medium flex-1">{node.name}</span>
          
          {getNodeBadge(node)}
          
{node.type === 'bill' && node.data && (
  <div className="flex items-center gap-2">
    <Button
      size="sm"
      variant="ghost"
      className="h-6 px-2"
      disabled={!node.data?.pdf_url}
      onClick={(e) => {
        e.stopPropagation();
        if (node.data?.pdf_url) {
          window.open(node.data.pdf_url, '_blank', 'noopener');
        }
      }}
      aria-label="Open PDF"
    >
      <FileText className="h-3 w-3 mr-1" /> PDF
    </Button>
    <Button
      size="sm"
      variant="ghost"
      className="h-6 px-2"
      disabled={!node.data?.csv_url}
      onClick={(e) => {
        e.stopPropagation();
        if (node.data?.csv_url) {
          window.open(node.data.csv_url, '_blank', 'noopener');
        }
      }}
      aria-label="Open CSV"
    >
      <FileSpreadsheet className="h-3 w-3 mr-1" /> CSV
    </Button>
  </div>
)}
        </div>

        {hasChildren && isExpanded && (
          <div className="space-y-1">
            {node.children.map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-muted-foreground">Loading archive hierarchy...</div>
        </CardContent>
      </Card>
    );
  }

  if (hierarchy.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center space-y-2">
            <FileText className="h-8 w-8 text-muted-foreground mx-auto" />
            <p className="text-muted-foreground">No archived bills found</p>
            <p className="text-sm text-muted-foreground">
              Bills will appear here once they are fully processed and archived
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Archive Hierarchy</h3>
            <Badge variant="outline">{hierarchy.length} customer{hierarchy.length !== 1 ? 's' : ''}</Badge>
          </div>
          
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {hierarchy.map(customer => renderNode(customer))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};